import { useEffect, useState } from "react";
import { Plus, Trash2, X, Package } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

export default function LotManager({ authAxios, materialName, defaultDate, onClose, onChange }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lotNumber, setLotNumber] = useState("");
  const [qty, setQty] = useState("");
  const [entryDate, setEntryDate] = useState(defaultDate || new Date().toISOString().substring(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/lots?material=${encodeURIComponent(materialName)}`);
      setLots(res.data);
    } catch (err) {
      toast.error("Eroare la încărcare loturi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialName]);

  const onAdd = async (e) => {
    e?.preventDefault?.();
    if (!lotNumber.trim() || !qty || parseFloat(qty) <= 0) {
      toast.warning("Completează nr. lot și cantitatea");
      return;
    }
    setSubmitting(true);
    try {
      await authAxios.post("/lots", {
        material_name: materialName,
        lot_number: lotNumber.trim(),
        entry_date: entryDate,
        original_qty: parseFloat(qty),
      });
      setLotNumber("");
      setQty("");
      await load();
      onChange?.();
      toast.success("Lot adăugat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la adăugare");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Ștergi acest lot?")) return;
    try {
      await authAxios.delete(`/lots/${id}`);
      await load();
      onChange?.();
      toast.success("Lot șters");
    } catch (err) {
      toast.error("Eroare la ștergere");
    }
  };

  const totalRemaining = lots.reduce((s, l) => s + (l.remaining_qty || 0), 0);
  const totalOriginal = lots.reduce((s, l) => s + (l.original_qty || 0), 0);
  const totalConsumed = lots.reduce((s, l) => s + (l.consumed_qty || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="lot-manager-modal"
      >
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between bg-emerald-50/40">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-700" />
            <div>
              <h3 className="text-sm font-bold text-neutral-900 capitalize">Loturi: {materialName}</h3>
              <p className="text-[11px] text-neutral-500">
                Total: {fmt(totalOriginal)} kg primite · {fmt(totalConsumed)} consum · <strong className="text-emerald-700">{fmt(totalRemaining)} kg rămase</strong>
              </p>
            </div>
          </div>
          <button data-testid="lot-close" onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add form */}
        <form onSubmit={onAdd} className="px-5 py-3 border-b border-neutral-200 bg-neutral-50/40 grid grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Nr. lot</label>
            <input
              data-testid="lot-number-input"
              type="text"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              placeholder="ex: 1"
              className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Cant (kg)</label>
            <input
              data-testid="lot-qty-input"
              type="number"
              step="any"
              min="0"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="50"
              className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Data intrare</label>
            <input
              data-testid="lot-date-input"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex items-end">
            <button
              data-testid="lot-add-button"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
            >
              <Plus className="w-3.5 h-3.5" />
              {submitting ? "..." : "Adaugă"}
            </button>
          </div>
        </form>

        {/* Lots list */}
        <div className="flex-1 overflow-y-auto thin-scroll">
          {loading && <div className="p-6 text-center text-xs text-neutral-400">Se încarcă...</div>}
          {!loading && lots.length === 0 && (
            <div className="p-8 text-center text-xs text-neutral-400">
              Niciun lot pentru această materie primă încă.
            </div>
          )}
          {!loading && lots.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                  <th className="px-4 py-2 w-8">#</th>
                  <th className="px-4 py-2">Lot</th>
                  <th className="px-4 py-2">Intrare</th>
                  <th className="px-4 py-2 text-right">Primit</th>
                  <th className="px-4 py-2 text-right">Consum</th>
                  <th className="px-4 py-2 text-right">Rămas</th>
                  <th className="px-4 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lots.map((lot, idx) => {
                  const isEmpty = lot.remaining_qty <= 0.0001;
                  const isCurrent = !isEmpty && idx === lots.findIndex((l) => l.remaining_qty > 0.0001);
                  return (
                    <tr key={lot.id} className={`border-t border-neutral-100 ${isCurrent ? "bg-emerald-50/50" : isEmpty ? "opacity-50" : ""}`}>
                      <td className="px-4 py-2 text-xs text-neutral-400 tabular-nums">{idx + 1}</td>
                      <td className="px-4 py-2 font-semibold text-neutral-900">
                        Lot {lot.lot_number}
                        {isCurrent && <span className="ml-2 text-[9px] font-bold text-emerald-700 uppercase tracking-wider">activ</span>}
                        {isEmpty && <span className="ml-2 text-[9px] font-medium text-neutral-400 uppercase tracking-wider">epuizat</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-neutral-500 tabular-nums">{lot.entry_date}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(lot.original_qty)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-amber-700">{fmt(lot.consumed_qty)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums font-bold ${isEmpty ? "text-neutral-400" : "text-emerald-700"}`}>{fmt(lot.remaining_qty)}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          data-testid={`lot-delete-${lot.id}`}
                          onClick={() => onDelete(lot.id)}
                          className="text-neutral-400 hover:text-red-600 p-1"
                          title="Șterge lot"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-2 border-t border-neutral-200 text-[10px] text-neutral-500 bg-neutral-50/30">
          💡 Consumul se deduce automat FIFO (din lotul cel mai vechi) la salvarea raportului zilnic.
        </div>
      </div>
    </div>
  );
}
